<template>
  <div
    :class="{
      'bl-target-progress-container-empty': !entries || !entries.length
    }"
    class="bl-target-progress-container"
  >
    <div v-if="readonly" class="relative w-full">
      <div class="flex-shrink-0 w-full h-14" />
      <div class="absolute w-full -top-6">
        <div class="flex items-center gap-2 px-4 py-2 bg-info-100">
          <div
            class="grid flex-shrink-0 w-10 h-10 place-items-center"
            :style="{ background: '#E4F1FA', borderRadius: '0.25rem' }"
          >
            <BlIcon icon="toast-info" class="w-6 h-6" />
          </div>
          <BlTextBody1
            class="text-info"
            text="All data displayed is mock information, used for post-session visual representation."
          />
        </div>
      </div>
    </div>

    <span
      v-if="(!entries || !entries.length) && !readonly"
      class="px-6 bl-target-progress-message"
    >
      <BlTextBody2
        text="Don't see any records yet? No problem! They'll start appearing as soon as you"
      />
      <div
        class="cursor-pointer "
        @click="$router.push({ path: `/clients/${$route.params.id}/session` })"
      >
        <BlTextSubtitle3
          class="bl-target-progress-message-highlight"
          text="start sessions."
        />
      </div>
    </span>

    <div v-if="entries.length" class="px-6 bl-target-progress-graph">
      <div v-if="chartLoading" class="absolute z-20 w-full h-full">
        <div class="absolute w-full h-full opacity-75 bg-pure-white"></div>
        <div class="absolute z-20 grid w-full h-full place-items-center">
          <div class="blum-loader"></div>
        </div>
      </div>

      <div
        v-if="target.type.includes('Sbt')"
        class="flex items-center gap-2 p-3 mb-5 bg-slate-200"
      >
        <div
          @click="taskTarget ? $emit('updateTaskTarget', null) : ''"
          :class="{ 'cursor-pointer': taskTarget }"
        >
          <BlTextSubtitle3
            :class="[taskTarget ? 'text-slate-600' : 'text-slate-700']"
            :text="target.name"
          />
        </div>
        <div>
          <BlTextSubtitle3 class="text-slate-600" text="/" v-if="taskTarget" />
        </div>
        <div>
          <BlTextSubtitle3
            v-if="taskTarget"
            class="text-slate-700"
            :text="`${taskTarget.code} - ${taskTarget.code_definition}`"
          />
        </div>
      </div>
      <div class="bl-target-progress-graph-header">
        <div class="bl-target-progress-graph-title-container">
          <BlTextSubtitle1
            class="bl-target-progress-graph-title"
            text="Progress"
          />
          <BlBubbleCount
            :count="entries.filter(i => !i.type.includes('PhaseLine')).length"
          />
          <BlSelectBox
            v-if="target.type.includes('Prompting')"
            :key="`select-${JSON.stringify(graphProgressByOptions)}`"
            class="w-40"
            dense
            v-model="graphProgressBy"
            :options="graphProgressByOptions"
            @change="graphProgressBy = $event"
          />
        </div>
        <div id="chart-legends" class="relative flex items-center gap-2">
          <BlTextBody2 text="Show" />
          <BlInputButton
            v-if="isTargetHasTrendline"
            label="Trendline"
            type="outlined"
            dense
            :class="{ 'border-light-purple': isTrendlineShow }"
            @clicked="isTrendlineShow = !isTrendlineShow"
          />
          <BlInputButton
            v-if="isTargetHasAverage"
            label="Average"
            type="outlined"
            dense
            :class="{ 'border-light-purple': isAverageShow }"
            @clicked="isAverageShow = !isAverageShow"
          />
          <BlInputButton
            label="Grid"
            type="outlined"
            dense
            :class="{ 'border-light-purple': isGridShow }"
            @clicked="isGridShow = !isGridShow"
          />
          <div class="relative">
            <BlInputButton
              icon="line-chart"
              label="View legends"
              type="outlined"
              dense
              class="border-light-purple"
              @clicked="isLegendPopupShow = !isLegendPopupShow"
            />
            <div
              v-if="isLegendPopupShow"
              :data-id="popupId"
              class="absolute right-0 z-50 mt-2 border rounded bg-pure-white border-slate-400"
            >
              <div
                :data-id="popupId"
                class="flex flex-col w-48 gap-2 px-3 py-3"
              >
                <BlTextSubtitle3
                  :data-id="popupId"
                  text="Legends"
                  class="text-slate-700"
                />
                <div
                  v-if="isTargetHasGoal"
                  :data-id="popupId"
                  class="flex items-center gap-2"
                >
                  <div
                    :data-id="popupId"
                    :style="{ backgroundColor: '#71C115' }"
                    class="flex-shrink-0 w-10 h-1 rounded"
                  />
                  <BlTextBody2 :data-id="popupId" text="Goal" />
                </div>
                <div
                  v-if="isTargetHasTrendline"
                  :data-id="popupId"
                  class="flex items-center gap-2"
                >
                  <div
                    :data-id="popupId"
                    :style="{
                      background:
                        'repeating-linear-gradient(90deg, #6750A4 0 8px, white 0 12px)'
                    }"
                    class="flex-shrink-0 w-10 h-1 rounded"
                  />
                  <BlTextBody2 :data-id="popupId" text="Trendline" />
                </div>
                <div
                  v-if="isTargetHasAverage"
                  :data-id="popupId"
                  class="flex items-center gap-2"
                >
                  <div
                    :data-id="popupId"
                    :style="{
                      background:
                        'repeating-linear-gradient(90deg, #71C115 0 8px, white 0 12px)'
                    }"
                    class="flex-shrink-0 w-10 h-1 rounded"
                  />
                  <BlTextBody2 :data-id="popupId" text="Average line" />
                </div>
                <!-- v-if="phaseLineCount > 0" -->
                <div :data-id="popupId" class="flex items-center gap-2">
                  <div
                    :data-id="popupId"
                    :style="{
                      background:
                        'repeating-linear-gradient(90deg, #E4E4E4 0 8px, white 0 12px)'
                    }"
                    class="flex-shrink-0 w-10 h-1 rounded"
                  />
                  <BlTextBody2 :data-id="popupId" text="Phase line" />
                </div>
                <div
                  v-if="
                    phaseLineCount === 0 &&
                      (!target.type.includes('Prompting') ||
                        graphProgressBy === 'final_score') &&
                      !target.type.includes('Sbt')
                  "
                  :data-id="popupId"
                  :title="target.name"
                  class="flex items-center gap-2"
                >
                  <div
                    :data-id="popupId"
                    :style="{ backgroundColor: '#9A8AC2' }"
                    class="flex-shrink-0 w-10 h-1 rounded"
                  />
                  <BlTextBody2
                    :data-id="popupId"
                    class="truncate"
                    :text="target.name"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="
          target.type.includes('Prompting') &&
            (graphProgressBy === 'frequency' ||
              graphProgressBy === 'percentage')
        "
        id="prompting-selection-legends"
      >
        <div
          class="flex items-center flex-shrink-0 w-full gap-3 px-5 py-2 overflow-x-auto scrollbar-lg"
        >
          <div
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              isPromptingShowAll ? 'border-light-purple-300' : 'border-grey-200'
            ]"
            @click="onTogglePromptingShow('all')"
          >
            <BlIcon icon="prompt-line" class="flex-shrink-0 h-4" />
            <BlTextBody2 text="All graphs" class="capitalize-first" />
          </div>
          <div
            v-for="prompt in defaultPrompts"
            :key="prompt.id"
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              prompt.is_show ? 'border-light-purple-300' : 'border-grey-200'
            ]"
            :title="prompt.name"
            @click="onTogglePromptingShow(prompt)"
          >
            <BlIcon
              v-if="prompt.shape && prompt.color"
              class="flex-shrink-0 w-auto h-4"
              :icon="'prompt-' + prompt.shape"
              :color="prompt.color"
            />
            <BlTextBody2
              :text="prompt.name"
              class="w-24 truncate capitalize-first"
            />
          </div>
        </div>
      </div>

      <div v-if="target.type.includes('Sbt')" id="task-selection-legends">
        <div
          class="flex items-center flex-shrink-0 w-full gap-3 px-5 py-2 overflow-x-auto scrollbar-lg"
        >
          <div
            v-if="taskTarget"
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              taskTarget.is_show && isPbShowAll
                ? 'border-light-purple-300'
                : 'border-grey-200'
            ]"
            @click="onToggleTaskTargetShow('all')"
          >
            <BlIcon icon="prompt-line" class="flex-shrink-0 h-4" />
            <BlTextBody2 text="All Graphs" class="capitalize-first" />
          </div>
          <div
            v-if="!taskTarget"
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              isTaskShowAll ? 'border-light-purple-300' : 'border-grey-200'
            ]"
            @click="onToggleTaskShow('allTasks', 'task')"
          >
            <BlIcon icon="prompt-line" class="flex-shrink-0 h-4" />
            <BlTextBody2 text="All Tasks" class="capitalize-first" />
          </div>
          <div
            v-if="!taskTarget"
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 overflow-hidden flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              isPbShowAll ? 'border-light-purple-300' : 'border-grey-200'
            ]"
            @click="onToggleTaskShow('allProblemBehaviors', 'pb')"
          >
            <BlIcon icon="prompt-line" class="flex-shrink-0 h-4" />
            <BlTextBody2
              text="All Problem Behaviors"
              class="truncate capitalize-first"
              :style="{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '70px'
              }"
              title="All Problem Behaviors"
            />
          </div>
          <div
            v-if="taskTarget"
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              taskTarget.is_show ? 'border-light-purple-300' : 'border-grey-200'
            ]"
            :title="taskTarget.code"
            @click="onToggleTaskTargetShow()"
          >
            <div class="h-4">
              <div
                class="flex-shrink-0 w-12"
                :style="{ backgroundColor: taskTarget.color, height: '2px' }"
              />
            </div>
            <BlTextBody2
              :text="taskTarget.code"
              class="w-24 truncate capitalize-first"
            />
          </div>
          <div
            v-if="!taskTarget"
            v-for="task in defaultTasks"
            :key="`task-${task.id}`"
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              task.is_show ? 'border-light-purple-300' : 'border-grey-200'
            ]"
            :title="task.code"
            @click="onToggleTaskShow(task, 'task')"
          >
            <div class="h-4">
              <div
                class="flex-shrink-0 w-12"
                :style="{ backgroundColor: task.color, height: '2px' }"
              />
            </div>
            <BlTextBody2
              :text="task.code"
              class="w-24 truncate capitalize-first"
            />
          </div>
          <div
            v-for="pb in defaultProblemBehaviors"
            :key="`problem-behavior-${pb.id}`"
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              pb.is_show ? 'border-light-purple-300' : 'border-grey-200'
            ]"
            :title="pb.code"
            @click="onToggleTaskShow(pb, 'pb')"
          >
            <div class="h-4 mb-1">
              <div
                class="flex-shrink-0 w-5"
                :style="{
                  backgroundColor: pb.color,
                  height: '14px',
                  borderRadius: '2px'
                }"
              />
            </div>
            <BlTextBody2
              :text="pb.code"
              class="w-24 truncate capitalize-first"
            />
          </div>
        </div>
      </div>

      <ProgressGraph
        :key="`${target.id}-${chartKey}-scrollable`"
        :graphData="chartDatas"
        :targetType="target.type"
        :isGridShow="isGridShow"
        :present="graphProgressBy"
      />
    </div>

    <div v-if="entries && entries.length" class="bl-target-progress-entries">
      <div class="flex items-center justify-between px-6 py-5">
        <div class="flex items-center gap-4">
          <BlTextSubtitle1 text="Session records" />
          <div
            v-if="target.type.includes('Prompting')"
            id="toggle-score-precentage"
            class="p-0.5 flex justify-between border-2 border-slate-400 rounded text-slate-700 bg-slate-400 text-sm font-bold"
          >
            <div
              v-for="opt in entryProgressByOptions"
              :key="opt.value"
              class="flex items-center justify-center h-7.5 px-4 rounded cursor-pointer"
              :class="[
                entryProgressBy === opt.value ? 'bg-pure-white' : 'bg-slate-400'
              ]"
              :title="opt.title"
              @click="entryProgressBy = opt.value"
            >
              {{ opt.label }}
            </div>
          </div>
        </div>
        <div v-if="!readonly" class="flex items-center gap-4">
          <div
            v-if="!target.type.includes('Sbt')"
            :title="!phaseLineCount ? 'Must have phase line to rearrange' : ''"
          >
            <BlInputButton
              type="outlined"
              @clicked="rearrangeState = true"
              :disabled="!phaseLineCount"
              label="Rearrange"
            />
          </div>
          <BlInputButton
            v-if="!target.type.includes('Sbt')"
            icon="plus"
            @clicked="newPhaseLineOpened = true"
            label="Phase line"
          />
        </div>
      </div>
      <div v-if="rearrangeState" class="absolute z-10 w-full px-6">
        <div
          class="flex items-center justify-between w-full p-3 border bg-grey-100 rounded-xl border-grey-200"
        >
          <BlTextBody1
            text="To rearrange the phase, drag the box and position it between the data."
            class="text-grey-400"
          />
          <div class="flex items-center gap-4">
            <BlInputButton
              type="plain"
              @clicked="rearrangeState = false"
              label="Close"
            />
            <!-- <BlInputButton
              @clicked="onUpdatePhaseLine"
              label="Update"
            /> -->
          </div>
        </div>
      </div>
      <div class="relative pb-40 overflow-x-auto scrollbar-lg">
        <div id="entries-header" class="flex items-center flex-grow">
          <div class="sticky left-0 z-10 flex pl-6 bg-pure-white">
            <div
              id="date-header"
              class="flex items-center flex-shrink-0 w-32 h-12 px-2 border-b border-slate-400 bg-pure-white"
            >
              <BlTextBody1 text="Date" />
            </div>
          </div>
          <div
            v-if="
              target.type.includes('Percentage') || target.type.includes('Pir')
            "
            id="score-header"
            class="flex items-center flex-shrink-0 w-16 h-12 px-2 border-b border-slate-400 bg-pure-white"
          >
            <BlTextBody1 text="Score" />
          </div>
          <div
            v-if="target.type.includes('Pir')"
            id="percentage-header"
            class="flex items-center flex-shrink-0 h-12 px-2 border-b w-28 border-slate-400 bg-pure-white"
          >
            <BlTextBody1 text="Percentage" />
          </div>
          <div
            v-if="target.type.includes('Duration')"
            id="duration-header"
            class="flex items-center flex-shrink-0 w-32 h-12 px-2 border-b border-slate-400 bg-pure-white"
          >
            <BlTextBody1 text="Recorded time" />
          </div>
          <div
            v-if="target.type.includes('Frequency')"
            id="frequency-header"
            class="flex items-center flex-shrink-0 w-24 h-12 px-2 border-b border-slate-400 bg-pure-white"
          >
            <BlTextBody1 text="Frequency" />
          </div>
          <div
            v-if="target.type.includes('Prompting')"
            id="prompts-header"
            class="flex items-center flex-shrink-0"
          >
            <div
              v-if="entryProgressBy === 'final_score'"
              class="flex items-center flex-shrink-0 h-12 px-2 border-b w-28 border-slate-400 bg-pure-white"
            >
              <BlTextBody1 text="Final score" />
            </div>
            <div
              v-else
              v-for="prompt in defaultPrompts"
              :key="prompt.id"
              class="flex items-center justify-center flex-shrink-0 h-12 px-2 transition-all duration-300 border-b border-slate-400 bg-pure-white"
              :class="{
                'w-12': entryProgressBy === 'score',
                'w-16': entryProgressBy === 'percentage'
              }"
            >
              <BlTextBody1 :text="prompt.abbreviation" />
            </div>
          </div>
          <div
            v-if="target.type.includes('Sbt') && !taskTarget"
            id="task-header"
            class="flex items-center flex-shrink-0"
          >
            <div
              v-for="task in defaultTasks"
              :key="task.id"
              class="flex items-center justify-center flex-shrink-0 w-16 h-12 px-2 border-b border-slate-400 bg-pure-white"
            >
              <BlTextBody1 :text="task.code" />
            </div>
          </div>
          <div
            v-if="target.type.includes('Sbt') && taskTarget"
            id="task-header"
            class="flex items-center flex-shrink-0"
          >
            <div
              class="flex items-center justify-center flex-shrink-0 w-16 h-12 px-2 border-b border-slate-400 bg-pure-white"
            >
              <BlTextBody1 :text="taskTarget.code" />
            </div>
          </div>
          <div
            v-if="target.type.includes('Sbt') && taskTarget"
            id="problem-behavior-header"
            class="flex items-center flex-shrink-0"
          >
            <div
              v-for="pb in defaultProblemBehaviors"
              :key="pb.id"
              class="flex items-center justify-center flex-shrink-0 w-16 h-12 px-2 border-b border-slate-400 bg-pure-white"
            >
              <BlTextBody1 :text="pb.code" />
            </div>
          </div>
          <div
            id="recorded-by-header"
            class="flex items-center flex-shrink-0 w-32 h-12 px-2 border-b border-slate-400 bg-pure-white"
          >
            <BlTextBody1 text="Recorded by" />
          </div>
          <div
            id="comment-header"
            v-if="!taskTarget"
            class="flex items-center flex-grow flex-shrink-0 h-12 px-2 border-b w-80 border-slate-400 bg-pure-white"
          >
            <BlTextBody1 text="Comment" />
          </div>
          <div
            id="menu-header"
            class="sticky right-0 z-10 flex pr-6 bg-pure-white"
          >
            <div
              class="flex items-center flex-shrink-0 w-12 h-12 px-4 border-b border-slate-400 bg-pure-white"
            ></div>
          </div>
        </div>
        <draggable
          handle=".handle"
          @update.prevent="onMovePosition($event)"
          class="flex flex-col"
          :class="{ 'pointer-events-none': readonly }"
        >
          <div
            v-for="entry in entries"
            :key="`entry-${entry.id}`"
            :id="entry.id"
          >
            <EntryPhaseLine
              v-if="entry.type.includes('PhaseLine')"
              :key="`phase-line-entry-${entry.id}-${entry.position}`"
              :entry="entry"
              :rearrangeState="rearrangeState"
              :isPrompting="target.type.includes('Prompting')"
            />
            <EntryFrequency
              v-else-if="entry.type.includes('Frequency')"
              :key="`frequency-entry-${entry.id}-${entry.position}`"
              :entry="entry"
            />
            <EntryPrompt
              v-else-if="entry.type.includes('Prompting')"
              :key="`prompting-entry-${entry.id}-${entry.position}`"
              :entry="entry"
              :promptColors="promptColors"
              :prompts="defaultPrompts"
              :progressBy="entryProgressBy"
              :format="target.prompting_format"
            />
            <EntryPromptSbt
              v-else-if="entry.type.includes('Sbt')"
              :key="`prompting-sbt-entry-${entry.id}-${entry.position}`"
              :entry="entry"
              :promptColors="promptSbtColors"
              :tasks="defaultTasks"
              :problemBehaviors="defaultProblemBehaviors"
              :prompts="defaultPrompts"
              @select-task="selectTask"
              :taskTarget="taskTarget"
              dataFormat="prompt_score"
            />
            <Entry
              v-else
              :key="`other-entry-${entry.id}-${entry.position}`"
              :entry="entry"
            />
          </div>
        </draggable>
      </div>
      <BlTextBody2
        class="w-full px-6 my-6 text-center"
        text="You have reached the end."
        style="opacity: 0.375"
      />
    </div>

    <NewPhaseLineModal
      v-if="newPhaseLineOpened"
      :targetId="target.id"
      :hasPhaseLine="phaseLineCount > 0"
      :isPrompting="target.type.includes('Prompting')"
      @close-modal="newPhaseLineOpened = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import draggable from "vuedraggable";

import ProgressGraph from "./ProgressGraph.vue";
import Entry from "./Entry.vue";
import EntryFrequency from "./EntryFrequency.vue";
import EntryPrompt from "./EntryPrompt.vue";
import EntryPromptSbt from "./EntryPromptSbt.vue";
import EntryPhaseLine from "./EntryPhaseLine.vue";
import NewPhaseLineModal from "./modal/NewPhaseLineModal.vue";

import BlBubbleCount from "../../BlBubbleCount.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "TargetPropertyProgress",
  components: {
    draggable,
    ProgressGraph,
    Entry,
    EntryFrequency,
    EntryPrompt,
    EntryPromptSbt,
    EntryPhaseLine,
    NewPhaseLineModal,
    BlBubbleCount,
    BlTextBody1,
    BlTextBody2,
    BlTextSubtitle1,
    BlTextSubtitle3,
    BlInputButton,
    BlSelectBox,
    BlIcon
  },
  // props: ["target", "graphEntries", "entries", "readonly", "taskTarget"],
  props: {
    target: { type: Object },
    taskTarget: { type: Object, default: null },
    graphEntries: { type: Object, default: () => {} },
    entries: { type: Array },
    readonly: { type: Boolean, default: false } // used for reviewd import from databank
  },
  data() {
    return {
      chartLoading: false,
      chartKey: "lorem-ipsum",
      chartDatas: {
        labels: [],
        datasets: [],
        recorded_by: []
      },
      chartTrendlineDatas: [],
      chartAverageDatas: [],
      //
      isTrendlineShow: true,
      isAverageShow: true,
      isGridShow: true,
      isLegendPopupShow: false,
      popupId: "chart-legends-popup",
      //
      defaultPrompts: [],
      defaultTasks: [],
      defaultProblemBehaviors: [],
      //
      graphProgressBy: "frequency", // frequency | percentage | final_score
      entryProgressBy: "score", // score | percentage | final_score
      //
      rearrangeState: false,
      newPhaseLineOpened: false,
      phaseLineColors: {
        lake: "#2AB1A8",
        tangerine: "#F57E00",
        cherry: "#FF4447",
        sky: "#487ADF",
        moss: "#A7C957",
        gold: "#F4D430",
        salmon: "#F4855D",
        primary: "#9A8AC2"
      },
      promptColors: {
        cherry: {
          primaryColor: "#FF4447",
          secondaryColor: "#FF4447",
          textColor: "#FFFFFF"
        },
        blush: {
          primaryColor: "#FFC1C2",
          secondaryColor: "#FF4447",
          textColor: "#FF4447"
        },
        gold: {
          primaryColor: "#F4D430",
          secondaryColor: "#F4D430",
          textColor: "#FFFFFF"
        },
        daffodil: {
          primaryColor: "#FBEEAC",
          secondaryColor: "#F4D430",
          textColor: "#9B7600"
        },
        lake: {
          primaryColor: "#2AB1A8",
          secondaryColor: "#2AB1A8",
          textColor: "#FFFFFF"
        },
        mint: {
          primaryColor: "#B8E5E2",
          secondaryColor: "#2AB1A8",
          textColor: "#2AB1A8"
        },
        sky: {
          primaryColor: "#487ADF",
          secondaryColor: "#487ADF",
          textColor: "#FFFFFF"
        },
        hydrangeas: {
          primaryColor: "#D3DFF7",
          secondaryColor: "#487ADF",
          textColor: "#487ADF"
        },
        grey: {
          primaryColor: "#E4E4E4",
          secondaryColor: "#A0A0A0",
          textColor: "#A0A0A0"
        },
        primary: {
          primaryColor: "#9A8AC2",
          secondaryColor: "#9A8AC2",
          textColor: "#FFFFFF"
        }
      },
      promptSbtColors: {
        moss: {
          primaryColor: "#A7C957",
          secondaryColor: "#A7C957",
          textColor: "#FFFFFF"
        },
        salmon: {
          primaryColor: "#F4855D",
          secondaryColor: "#F4855D",
          textColor: "#FFFFFF"
        },
        gold: {
          primaryColor: "#F4D430",
          secondaryColor: "#F4D430",
          textColor: "#FFFFFF"
        },
        lake: {
          primaryColor: "#2AB1A8",
          secondaryColor: "#2AB1A8",
          textColor: "#FFFFFF"
        },
        cherry: {
          primaryColor: "#FF4447",
          secondaryColor: "#FF4447",
          textColor: "#FFFFFF"
        },
        sky: {
          primaryColor: "#487ADF",
          secondaryColor: "#487ADF",
          textColor: "#FFFFFF"
        },
        lavender: {
          primaryColor: "#A533FF",
          secondaryColor: "#A533FF",
          textColor: "#FFFFFF"
        },
        tangerine: {
          primaryColor: "#F57E00",
          secondaryColor: "#F57E00",
          textColor: "#FFFFFF"
        }
      },
      promptPoints: {
        square: { pointStyle: "rectRounded" },
        circle: { pointStyle: "circle" },
        triangle: { pointStyle: "triangle" },
        diamond: { pointStyle: "rectRot" }
      }
    };
  },
  computed: {
    ...mapGetters(["client"]),
    isPromptingShowAll() {
      let isShow = true;
      this.defaultPrompts.forEach(i => {
        if (!i.is_show) isShow = false;
      });
      return isShow;
    },
    isTaskShowAll() {
      let isShow = true;
      this.defaultTasks.forEach(i => {
        if (!i.is_show) isShow = false;
      });
      return isShow;
    },
    isPbShowAll() {
      let isShow = true;
      this.defaultProblemBehaviors.forEach(i => {
        if (!i.is_show) isShow = false;
      });
      return isShow;
    },
    phaseLineCount() {
      const phaseLines = this.entries.filter(i => i.type.includes("PhaseLine"));
      return phaseLines.length;
    },
    isTargetHasTrendline() {
      if (this.target.type.includes("Prompting")) {
        if (this.graphProgressBy === "final_score") return true;
        const isShowPrompts = [...this.defaultPrompts].filter(i => i.is_show);
        if (isShowPrompts.length === 1) return true;
        return false;
      }
      if (this.target.type.includes("Sbt")) return false;
      return true;
    },
    isTargetHasAverage() {
      if (this.target.type.includes("Prompting")) return false;
      if (this.target.type.includes("Sbt")) return false;
      if (this.phaseLineCount > 0) return false;
      return true;
    },
    isTargetHasGoal() {
      if (!this.target) return false;
      if (!this.target.type) return false;
      if (this.target.type.includes("Prompting")) {
        if (
          this.graphProgressBy === "frequency" &&
          this.target.prompting_format === "classis"
        ) {
          return true;
        }
        if (
          this.graphProgressBy === "final_score" &&
          this.target.prompting_format === "custom"
        ) {
          return true;
        }
        return false;
      }
      if (this.target.type.includes("Sbt")) {
        return false;
      }
      return true;
    },
    //
    graphProgressByOptions() {
      const arr = [
        { value: "frequency", label: "By frequency" },
        { value: "percentage", label: "By percentage" }
      ];
      if (this.target.prompting_format === "custom") {
        arr.unshift({
          value: "final_score",
          label: "By final score"
        });
      }
      return arr;
    },
    entryProgressByOptions() {
      const arr = [
        {
          value: "score",
          label: "Frequency",
          title: "Frequency present"
        },
        {
          value: "percentage",
          label: "Percentage",
          title: "Percentage present"
        }
      ];
      if (this.target.prompting_format === "custom") {
        arr.unshift({
          value: "final_score",
          label: "Final score",
          title: "Final score present"
        });
      }
      return arr;
    }
  },
  watch: {
    target(val, oldVal) {
      if (val.prompting_format !== oldVal.prompting_format) {
        this.graphProgressBy = "frequency";
        this.entryProgressBy = "score";
      }
      this.initiateGraph(val);
    },
    graphEntries: {
      deep: true,
      handler(val) {
        this.generateGraphEntriesData(val);
      }
    },
    isTrendlineShow(val) {
      if (this.chartTrendlineDatas.length) {
        if (val) {
          this.chartTrendlineDatas.forEach(i => {
            this.chartDatas.datasets.push(i);
          });
        } else {
          this.chartTrendlineDatas.forEach(() => {
            this.chartDatas.datasets = this.chartDatas.datasets.filter(
              i => !i.key || i.key !== "trendline"
            );
          });
        }
        this.onResetChartKey();
      }
    },
    isAverageShow(val) {
      if (this.chartAverageDatas.length) {
        if (val) {
          this.chartAverageDatas.forEach(i => {
            this.chartDatas.datasets.push(i);
          });
        } else {
          this.chartAverageDatas.forEach(() => {
            this.chartDatas.datasets = this.chartDatas.datasets.filter(
              i => !i.key || i.key !== "average"
            );
          });
        }
        this.onResetChartKey();
      }
    },
    isGridShow() {
      this.onResetChartKey();
    },
    isLegendPopupShow(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closePopupHandler);
        }, 10);
      }
    },
    //
    async graphProgressBy(val) {
      // readonly (reviewed)
      if (this.readonly) {
        this.generateGraphEntriesData(this.graphEntries);
        return;
      }
      let params = "";
      if (this.target.type.includes("Prompting")) {
        params += `?progress_by=${val}`;
      }
      await this.fetchGraphEntries(params);
    },
    taskTarget() {
      if (this.taskTarget) {
        this.taskTarget.is_show = true;
        this.defaultProblemBehaviors.forEach(i => {
          if (!i.is_show) i.is_show = true;
        });
      }
      this.generateGraphEntriesData(this.graphEntries);
    }
  },
  async mounted() {
    this.initiateGraph(this.target);

    // trigger generate graph for readonly (reviewed)
    if (this.readonly) {
      this.graphProgressBy = "percentage";
      setTimeout(() => {
        this.graphProgressBy = "frequency";
      }, 100);
    }
  },
  methods: {
    ...mapActions(["getGraphEntries", "movePhaseLine"]),
    initiateGraph(target) {
      let params = "";
      if (target.type.includes("Prompting")) {
        // this.graphProgressBy = "frequency";
        // this.entryProgressBy = "score";
        params += `?progress_by=${this.graphProgressBy}`;
        this.generatePrompts(target.prompts);
      }
      if (target.type.includes("Sbt")) {
        this.generatePrompts(target.prompts);
        this.generateTasks(target.target_tasks);
        this.generateProblemBehaviors(target.target_problem_behaviors);
        if (this.taskTarget) {
          this.taskTarget.is_show = true;
        }
      }

      this.fetchGraphEntries(params);
    },
    async fetchGraphEntries(params = "") {
      if (this.readonly) return;
      this.chartLoading = true;
      const { success, data } = await this.getGraphEntries({
        target_id: this.target.id,
        params
      });
      this.chartLoading = false;
      if (!success) return;
      this.generateGraphEntriesData(data);
    },
    generatePrompts(val = []) {
      const arr = [...val]
        .sort((a, b) => a.position - b.position)
        .sort((a, b) => {
          // sort by highest score
          if (this.target.prompting_format === "custom") {
            if (a.is_default && a.abbreviation === "Id") return -1;
            if (b.is_default && b.abbreviation === "Id") return 1;

            if (a.is_default && a.abbreviation === "Ic") return 1;
            if (b.is_default && b.abbreviation === "Ic") return -1;
            return b.score - a.score;
          }
          return 1;
        });
      this.defaultPrompts = arr.map(i => ({ ...i, is_show: true }));
    },
    generateTasks(val = []) {
      this.defaultTasks = val.map(i => ({ ...i, is_show: true }));
    },
    generateProblemBehaviors(val = []) {
      this.defaultProblemBehaviors = val.map(i => ({ ...i, is_show: true }));
    },
    generateGraphEntriesData({
      labels = [],
      datasets = [],
      recorded_by: recordedBy = []
    }) {
      // generate datasets
      let newDatasets = [];
      if (datasets.length) {
        if (
          this.target.type.includes("Prompting") &&
          this.graphProgressBy !== "final_score"
        ) {
          // promting with frequency and percentage progress_by
          // prepare prompting property
          newDatasets = this.defaultPrompts.map(i => {
            return {
              promptId: i.id,
              label: i.name,
              data: [],
              // line style
              backgroundColor: this.promptColors[i.color].primaryColor,
              borderColor: this.promptColors[i.color].primaryColor,
              // point style
              pointStyle: this.promptPoints[i.shape].pointStyle,
              pointBackgroundColor: this.promptColors[i.color].primaryColor,
              pointBorderColor: this.promptColors[i.color].secondaryColor,
              pointRadius: 4,
              hoverRadius: 4,
              fill: false,
              tension: 0.1
            };
          });
          // input prompting data value
          datasets[0].data.forEach(entry => {
            this.defaultPrompts.forEach((prompt, idx) => {
              let val = null;
              if (prompt.is_show && entry) {
                val = entry[prompt.id];
                if (this.readonly) {
                  val = entry[prompt.id][this.graphProgressBy];
                }
              }
              newDatasets[idx].data.push(val);
            });
          });
          // add goal for prompting with frequency present
          if (
            this.graphProgressBy === "frequency" &&
            this.target.prompting_format === "classic"
          ) {
            const goalDataset = {
              label: datasets[1].label,
              data: datasets[1].data,
              backgroundColor: "#71C115",
              borderColor: "#71C115",
              pointRadius: 0,
              fill: false,
              tension: 0.1
            };
            newDatasets.push(goalDataset);
          }
        } else {
          // includes prompting with final_score progress_by
          newDatasets = datasets.map(i => {
            const color = i.color ? i.color : "primary"; // set 'primary' for default color
            const propertyColor =
              i.label === "Goal" ? "#71C115" : this.phaseLineColors[color];

            let data = i.data;
            // readonly (reviewed) use score for prompting
            if (
              this.readonly &&
              this.target.type.includes("Prompting") &&
              i.label !== "Goal"
            ) {
              data = i.score;
            }
            return {
              label: i.label,
              data: data,
              backgroundColor: propertyColor,
              borderColor: propertyColor,
              pointRadius: i.label === "Goal" ? 0 : 4,
              hoverRadius: 4,
              fill: false,
              tension: 0.1
            };
          });
        }
      }

      if (
        datasets &&
        datasets.data &&
        datasets.data.tasks &&
        datasets.data.problem_behaviors &&
        this.target.type.includes("Sbt")
      ) {
        if (this.taskTarget) {
          newDatasets = [
            {
              taskId: this.taskTarget.id,
              type: "line",
              label: this.taskTarget.code,
              data: [],
              backgroundColor: this.taskTarget.color,
              borderColor: this.taskTarget.color,
              pointBackgroundColor: this.taskTarget.color,
              pointBorderColor: this.taskTarget.color,
              pointRadius: 4,
              hoverRadius: 4,
              fill: false,
              tension: 0.1
            },
            ...this.defaultProblemBehaviors.map(i => ({
              type: "bar",
              behaviorId: i.id,
              label: i.code,
              data: [],
              backgroundColor: i.color,
              borderColor: i.color,
              yAxisID: "y-axis-bar",
              barPercentage: 10,
              barThickness: 100,
              maxBarThickness: 100,
              minBarLength: 10
            }))
          ];

          datasets.data.tasks.forEach(entry => {
            let val = null;
            if (this.taskTarget.is_show && entry && entry[this.taskTarget.id]) {
              val = entry[this.taskTarget.id].score;
            }
            newDatasets[0].data.push(val);
          });
          this.entries.forEach(entry => {
            if (typeof entry.scores !== "object" || entry.scores === null) {
              return;
            }

            if (this.taskTarget) {
              const problemBehaviorCounts = {};

              Object.values(entry.scores).forEach(score => {
                if (score.target_task_id === this.taskTarget.id) {
                  if (
                    score.problem_behavior_id &&
                    score.problem_behavior_code &&
                    score.target_problem_behavior_id
                  ) {
                    const pbKey = score.problem_behavior_code;
                    problemBehaviorCounts[pbKey] =
                      (problemBehaviorCounts[pbKey] || 0) + 1;
                  }
                }
              });

              this.defaultProblemBehaviors.forEach((behavior, idx) => {
                const count = problemBehaviorCounts[behavior.code] || 0;
                const val = count > 0 ? count : null;

                if (behavior.is_show && newDatasets && newDatasets[1 + idx]) {
                  newDatasets[1 + idx].data.push(val);
                }
              });
            }
          });
        } else {
          newDatasets = [
            ...this.defaultTasks.map(i => ({
              taskId: i.id,
              type: "line",
              label: i.code,
              data: [],
              backgroundColor: i.color,
              borderColor: i.color,
              pointBackgroundColor: i.color,
              pointBorderColor: i.color,
              pointRadius: 4,
              hoverRadius: 4,
              fill: false,
              tension: 0.1
            })),
            ...this.defaultProblemBehaviors.map(i => ({
              type: "bar",
              behaviorId: i.id,
              label: i.code,
              data: [],
              backgroundColor: i.color + "50",
              borderColor: i.color + "50",
              hoverBackgroundColor: i.color,
              yAxisID: "y-axis-bar",
              barPercentage: 10,
              barThickness: 100,
              maxBarThickness: 100,
              minBarLength: 10
            }))
          ];

          // Proses data untuk defaultTasks
          datasets.data.tasks.forEach(entry => {
            this.defaultTasks.forEach((task, idx) => {
              let val = null;
              if (task.is_show && entry && entry[task.id]) {
                val = entry[task.id].score;
              }
              newDatasets[idx].data.push(val);
            });
          });

          // Proses data untuk problem behaviors
          datasets.data.problem_behaviors.forEach(entry => {
            this.defaultProblemBehaviors.forEach((behavior, idx) => {
              let val = null;
              if (behavior.is_show && entry && entry[behavior.id]) {
                val = entry[behavior.id].score;
              }
              newDatasets[this.defaultTasks.length + idx].data.push(val);
            });
          });
        }
      }

      this.chartDatas = {
        labels: labels.map((i, idx) => {
          if (!i) return "";
          const split = i.split("-").reverse();
          if (split.length === 3) {
            return [`${split[0]}/${split[1]}`, split[2]];
          }
          return split.join("/");
        }),
        datasets: newDatasets,
        recorded_by: recordedBy,
        mixed: this.target.type === "Target::Sbt"
      };

      // generate trendline dataset
      this.chartTrendlineDatas = [];
      if (this.isTargetHasTrendline) {
        this.chartDatas.datasets.forEach(dataset => {
          // only generate non-goal label
          if (dataset.label !== "Goal") {
            const arr = { a: [], b: [], c: [] };
            let firstAttemp = false;
            let endOfFirtsAttemp = false;
            dataset.data.forEach((val, index) => {
              if (val === null && index === 0) {
                firstAttemp = true;
              }
              if (firstAttemp) {
                if (val === null && !endOfFirtsAttemp) {
                  arr.a.push(val);
                } else if (val !== null) {
                  endOfFirtsAttemp = true;
                  arr.b.push(val);
                } else if (val === null) arr.c.push(val);
              } else {
                if (val !== null) arr.b.push(val);
                else if (val === null) arr.c.push(val);
              }
            });
            // start generate trendline
            const idx = arr.b.length - 1;
            const dt = [
              arr.b[0],
              ...this.interpolate(arr.b[0], arr.b[idx], idx - 1),
              arr.b[idx]
            ];
            // remove the trendline if there are only 2 points
            if (dt.length === 2) {
              dt.pop();
            }
            const res = [...arr.a, ...dt, ...arr.c];
            const trendline = {
              key: "trendline",
              label: "Trendline",
              data: res,
              borderColor: "#6750A4",
              backgroundColor: "rgba(0, 0, 0, 0)",
              borderWidth: 2,
              borderDash: [12],
              pointRadius: 0
            };
            this.chartTrendlineDatas.push(trendline);
          }
        });
      }

      // generate average dataset
      this.chartAverageDatas = [];
      if (this.isTargetHasAverage) {
        this.chartDatas.datasets.forEach(dataset => {
          // only generate non-goal label
          if (dataset.label !== "Goal") {
            let totalScore = 0;
            let totalCount = 0;
            dataset.data.forEach((val, index) => {
              if (val !== null) {
                totalScore += val;
                totalCount += 1;
              }
            });
            // start generate average
            const average = Number((totalScore / totalCount).toFixed(0));
            const res = Array(totalCount).fill(average);
            const averageLine = {
              key: "average",
              label: "Average",
              data: res,
              borderColor: "#71C115",
              backgroundColor: "rgba(0, 0, 0, 0)",
              borderWidth: 2,
              borderDash: [12],
              pointRadius: 2
            };
            this.chartAverageDatas.push(averageLine);
          }
        });
      }

      if (this.isTrendlineShow) {
        this.chartTrendlineDatas.forEach(i => {
          this.chartDatas.datasets.push(i);
        });
      }

      if (this.isAverageShow) {
        this.chartAverageDatas.forEach(i => {
          this.chartDatas.datasets.push(i);
        });
      }

      if (this.phaseLineCount > 0) this.isGridShow = false;
      else this.isGridShow = true;

      this.onResetChartKey();
    },
    //
    onResetChartKey() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      this.chartKey = combination;
    },
    //
    onTogglePromptingShow(prompt = "all") {
      if (prompt === "all") {
        const result = !this.isPromptingShowAll;
        this.defaultPrompts = this.defaultPrompts.map(i => ({
          ...i,
          is_show: result
        }));
      } else {
        const idx = this.defaultPrompts.findIndex(i => i.id === prompt.id);
        if (idx > -1) {
          this.defaultPrompts[idx].is_show = !this.defaultPrompts[idx].is_show;
        }
      }
      this.generateGraphEntriesData(this.graphEntries);
    },
    onToggleTaskShow(data = "", type = "") {
      if (type === "task" && data === "allTasks") {
        const result = !this.isTaskShowAll;
        this.defaultTasks = this.defaultTasks.map(i => ({
          ...i,
          is_show: result
        }));
      } else if (type === "pb" && data === "allProblemBehaviors") {
        const result = !this.isPbShowAll;
        this.defaultProblemBehaviors = this.defaultProblemBehaviors.map(i => ({
          ...i,
          is_show: result
        }));
      } else if (
        type === "task" &&
        data !== "allTasks" &&
        data !== "taskTarget"
      ) {
        const idx = this.defaultTasks.findIndex(i => i.id === data.id);
        if (idx > -1) {
          this.defaultTasks[idx].is_show = !this.defaultTasks[idx].is_show;
        }
      } else if (type === "pb" && data !== "allProblemBehaviors") {
        const idx = this.defaultProblemBehaviors.findIndex(
          i => i.id === data.id
        );
        if (idx > -1) {
          this.defaultProblemBehaviors[idx].is_show = !this
            .defaultProblemBehaviors[idx].is_show;
        }
      }
      this.generateGraphEntriesData(this.graphEntries);
    },
    onToggleTaskTargetShow(data = "") {
      if (data === "all") {
        const allActive = this.taskTarget.is_show && this.isPbShowAll;
        const newState = !allActive;

        this.defaultProblemBehaviors = this.defaultProblemBehaviors.map(i => ({
          ...i,
          is_show: newState
        }));

        this.taskTarget.is_show = newState;
      } else {
        this.taskTarget.is_show = !this.taskTarget.is_show;
      }
      this.generateGraphEntriesData(this.graphEntries);
    },
    //
    selectTask(task) {
      this.$emit("select-task", task);
    },
    //
    onMovePosition(event) {
      if (this.readonly) return;
      const data = {
        id: Number(event.item.id),
        position: event.newIndex + 1,
        target_id: this.target.id
      };
      this.movePhaseLine(data);
    },
    //
    getSeconds(duration) {
      const [h, m, s] = duration.split(":");
      if (!h || !m || !s) return 0;
      return Number(h) * 3600 + Number(m) * 60 + Number(s);
    },
    formatTime(x) {
      let hours = "00";
      let minutes = "00";
      let seconds = "00";
      if (typeof x === "number") {
        seconds = (x % 60).toString().padStart(2, "0");
        if (x >= 60) {
          minutes = Math.floor(x / 60);
          if (x >= 3600) minutes = minutes % 60;
          minutes = minutes.toString().padStart(2, "0");
        }
        if (x >= 3600) {
          hours = Math.floor(x / 3600)
            .toString()
            .padStart(2, "0");
        }
      }
      return `${hours}:${minutes}:${seconds}`;
    },
    interpolate(first, last, count) {
      const interpolatedPoints = [];

      // Calculate the difference between start and end coordinates
      const deltaX = (last - first) / (count + 1);

      // Interpolate the points
      for (let i = 1; i <= count; i++) {
        const interpolatedX = Number(first + deltaX * i);
        interpolatedPoints.push(interpolatedX);
      }

      return interpolatedPoints;
    },
    closePopupHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.popupId) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closePopupHandler);
        this.isLegendPopupShow = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.bl-target-progress-container {
  position: relative;
  width: 100%;
  // height: calc(100% - 3.5625rem);
  height: 100%;
  padding: 1.5rem 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  &.bl-target-progress-container-empty {
    padding: 0 30%;
    justify-content: center;
    align-items: center;
  }
  .bl-target-progress-message {
    position: relative;
    width: 100%;
    text-align: center;
    .bl-target-progress-message-highlight {
      cursor: pointer;
      position: relative;
      color: var(--primary-color);
    }
  }
  .bl-target-progress-graph {
    position: relative;
    width: 100%;
    display: flex;
    gap: 1rem;
    flex-direction: column;
    .bl-target-progress-graph-header {
      position: relative;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      .bl-target-progress-graph-title-container {
        position: relative;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        .bl-target-progress-graph-count {
          position: relative;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 0.75rem;
          background-color: var(--primary-color);
          margin-left: 0.5rem;
          font-weight: 600;
          font-size: 0.75rem;
          line-height: 1;
          color: #fff;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
    }
    .bl-target-progres-graph-body {
      position: relative;
      width: 100%;
      height: 30rem;
    }
  }
  .bl-target-progress-entries {
    margin-top: 2rem;
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/clients/target/PropertyProgress.vue